import { ChangeTypes } from '@design/utils/constants';
import { SystemDesignObject } from './system-design-object.abstract.model';
import { ConfigurationIds } from './ui-state.model';
import { IPosition } from './trees.model';
import { Vertex } from './vertex.model';

/**
 * Physical equipment placed on the design canvas for constructability review
 * and PVCAD export. Distinct from the quote-level `battery`/`inverter` stores
 * (which model *hardware selection*): a DesignComponent is the *physical
 * location* of a Storage or Inverter unit on the site.
 *
 * Placement + rendering deliberately mirror the Obstacle system — a rooftop
 * component behaves like an obstacle (panel keep-out, red-when-overflown), and
 * the geometry is a rectangle in 2D / cuboid in 3D.
 */
export enum ComponentType {
  storage = 'storage',
  inverter = 'inverter',
}

export enum MountingType {
  /** Sits on the roof surface — acts like an obstacle (panel keep-out). */
  rooftop = 'rooftop',
  /** Snaps to the outside building wall, 3ft above ground. */
  wallMounted = 'wall-mounted',
  /** Placed on the ground only. */
  groundMount = 'ground-mount',
}

/**
 * Default dimensions (feet) per component type AND mounting type — the PRD's
 * fallback table. Used when the model dimensions can't be resolved from the
 * quote hardware / DB, no hardware is chosen yet, or the chosen inverter is a
 * micro-inverter. Ground-mount defaults are containerized C&I sizes.
 */
export const DEFAULT_COMPONENT_DIMENSIONS: Record<
  ComponentType,
  Record<MountingType, { length: number; width: number; height: number }>
> = {
  [ComponentType.inverter]: {
    [MountingType.wallMounted]: { length: 3, width: 1, height: 2.5 },
    [MountingType.rooftop]: { length: 4, width: 2, height: 6 },
    [MountingType.groundMount]: { length: 20, width: 8, height: 9 },
  },
  [ComponentType.storage]: {
    [MountingType.wallMounted]: { length: 3, width: 1, height: 4 },
    [MountingType.rooftop]: { length: 6, width: 3, height: 7 },
    [MountingType.groundMount]: { length: 30, width: 6, height: 9 },
  },
};

export const DEFAULT_COMPONENT_TYPE = ComponentType.storage;
export const DEFAULT_MOUNTING_TYPE = MountingType.rooftop;
/** Default edge-to-edge spacing kept between adjacent components (feet). */
export const DEFAULT_INTER_SPACING_FT = 3;

/** Display labels — the product calls the storage component "Storage". */
export const COMPONENT_TYPE_LABELS: Record<ComponentType, string> = {
  [ComponentType.storage]: 'Storage',
  [ComponentType.inverter]: 'Inverter',
};

export const defaultDimensionsFor = (
  type: ComponentType,
  mounting: MountingType
): { length: number; width: number; height: number } =>
  DEFAULT_COMPONENT_DIMENSIONS[type]?.[mounting] ??
  DEFAULT_COMPONENT_DIMENSIONS[DEFAULT_COMPONENT_TYPE][DEFAULT_MOUNTING_TYPE];

interface IDesignComponent {
  id: string;
  name: string;
  componentType: ComponentType;
  mountingType: MountingType;
  /** Roof this component is placed on — only meaningful for rooftop mounting. */
  roofId?: string;
  /** All roofs whose footprint the component overlaps (rooftop parity with obstacle). */
  roofAssociation?: Array<string>;
  position: IPosition;
  length: number;
  width: number;
  height: number;
  /** true when dimensions came from the DEFAULT map, not resolved hardware. */
  usingDefaultDimensions: boolean;
  rotation: number;
  /** Edge-to-edge spacing (feet) respected when snapping next to another component. */
  interSpacing: number;
  /** Quote hardware this component was resolved from (nullable — may be placed
   *  before hardware is chosen, or when the chosen inverter is a micro). */
  hardwareModelId?: string | null;
  manufacturer?: string | null;
  /** Human-readable hardware model name (for PVCAD export metadata). */
  modelName?: string | null;
  /** Renders red + flagged when the footprint spills into another structure. */
  overflown?: boolean;
  vertexIds?: Array<Vertex['id']>;
  changeType?: ChangeTypes;
}

export class DesignComponent
  extends SystemDesignObject
  implements IDesignComponent
{
  selectConfigId?: boolean | undefined;
  id: string;
  name: string;
  configurationId = ConfigurationIds.DesignComponent;
  componentType: ComponentType;
  mountingType: MountingType;
  roofId?: string;
  roofAssociation?: Array<string>;
  position: IPosition;
  length: number;
  width: number;
  height: number;
  usingDefaultDimensions: boolean;
  rotation: number;
  interSpacing: number;
  hardwareModelId?: string | null;
  manufacturer?: string | null;
  modelName?: string | null;
  overflown?: boolean;
  vertexIds?: Array<Vertex['id']>;
  changeType?: ChangeTypes;

  constructor(data: Omit<DesignComponent, 'configurationId'>) {
    super();
    this.id = data.id;
    this.name = data.name;
    this.componentType = data.componentType ?? DEFAULT_COMPONENT_TYPE;
    this.mountingType = data.mountingType ?? DEFAULT_MOUNTING_TYPE;
    this.roofId = data.roofId;
    this.roofAssociation = data.roofAssociation ?? [];
    this.position = data.position ?? { x: 0, y: 0, z: 0 };
    // Self-heal missing dimensions (e.g. partial data back from the quote
    // API round-trip) with the type+mounting defaults so the RHS + scene
    // never see undefined sizes.
    const dims = defaultDimensionsFor(this.componentType, this.mountingType);
    this.length = data.length ?? dims.length;
    this.width = data.width ?? dims.width;
    this.height = data.height ?? dims.height;
    this.usingDefaultDimensions = data.usingDefaultDimensions ?? true;
    this.rotation = data.rotation ?? 0;
    this.interSpacing = data.interSpacing ?? DEFAULT_INTER_SPACING_FT;
    this.hardwareModelId = data.hardwareModelId ?? null;
    this.manufacturer = data.manufacturer ?? null;
    this.modelName = data.modelName ?? null;
    this.overflown = data.overflown ?? false;
    this.vertexIds = data.vertexIds ?? [];
    this.changeType = data.changeType;
  }
}
